import type { HttpTypes } from "@medusajs/types";
import { memo, useCallback, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { AddressDialog } from "@/components/addresses";
import type {
  CheckoutAddressFormData,
  InlineAddressData
} from "../validations";
import { AddressDropdown } from "./AddressDropdown";
import { CheckoutAddressDialog } from "./CheckoutAddressDialog";
import { ContactInformation } from "./ContactInformation";

export interface ShippingAddressSectionProps {
  /** React Hook Form instance */
  form: UseFormReturn<CheckoutAddressFormData>;
  /** Customer addresses */
  addresses: HttpTypes.StoreCustomerAddress[];
  /** Whether addresses are loading */
  isLoading?: boolean;
  /** Whether the form is disabled (e.g., during submission) */
  disabled?: boolean;
  /**
   * Callback when address changes - used for real-time cart updates
   * Address changes affect shipping options availability and pricing
   */
  onAddressChange?: (
    address: HttpTypes.StoreCustomerAddress | InlineAddressData
  ) => void;
}

/**
 * Shipping address section for checkout
 *
 * Contains:
 * - Contact information form (email + phone)
 * - Address dropdown selector with saved addresses
 * - Add new address dialog with "save for later" option
 *
 * Supports both:
 * - Saved addresses (stored in customer account)
 * - One-time addresses (not saved, only used for this order)
 *
 * @example
 * ```tsx
 * <ShippingAddressSection
 *   form={form}
 *   addresses={addresses}
 *   isLoading={isLoading}
 * />
 * ```
 */
export const ShippingAddressSection = memo(function ShippingAddressSection({
  form,
  addresses,
  isLoading = false,
  disabled = false,
  onAddressChange
}: ShippingAddressSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] =
    useState<HttpTypes.StoreCustomerAddress | null>(null);

  const {
    formState: { errors },
    watch,
    setValue,
    trigger
  } = form;

  const shippingAddressId = watch("shippingAddressId");
  const shippingAddress = watch("shippingAddress");

  // Handle saved address selection
  const handleSelectAddress = useCallback(
    (addressId: string) => {
      // Clear any custom address when selecting a saved one
      setValue("shippingAddress", undefined, { shouldValidate: false });
      setValue("shippingAddressId", addressId, { shouldValidate: true });

      // Also update phone if the selected address has one
      const selectedAddress = addresses.find((a) => a.id === addressId);
      if (selectedAddress?.phone) {
        setValue("phone", selectedAddress.phone, { shouldValidate: false });
      }

      // Trigger cart update for shipping options refresh
      if (selectedAddress && onAddressChange) {
        onAddressChange(selectedAddress);
      }
    },
    [addresses, setValue, onAddressChange]
  );

  // Handle custom address re-selection (from dropdown)
  const handleSelectCustom = useCallback(() => {
    // Clear saved address ID to use custom address, then re-validate it - the
    // path the missing-address error lives on (see handleAddressSuccess)
    setValue("shippingAddressId", "", { shouldValidate: false });
    void trigger("shippingAddressId");
  }, [setValue, trigger]);

  // Handle add new address
  const handleAddNewAddress = useCallback(() => {
    setDialogOpen(true);
  }, []);

  // Handle dialog close
  const handleDialogClose = useCallback((open: boolean) => {
    setDialogOpen(open);
  }, []);

  // Handle edit existing address
  const handleEditAddress = useCallback(
    (address: HttpTypes.StoreCustomerAddress) => {
      setEditingAddress(address);
    },
    []
  );

  // Handle edit dialog close
  const handleEditDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setEditingAddress(null);
    }
  }, []);

  // Handle edit success - refresh the cart address if it's the selected one
  const handleEditSuccess = useCallback(
    (updatedAddress?: HttpTypes.StoreCustomerAddress) => {
      if (updatedAddress && onAddressChange) {
        onAddressChange(updatedAddress);
      }
    },
    [onAddressChange]
  );

  // Handle address creation/entry success
  const handleAddressSuccess = useCallback(
    (result: {
      savedAddressId?: string;
      inlineAddress?: InlineAddressData;
    }) => {
      if (result.savedAddressId) {
        // Saved address - select it by ID
        setValue("shippingAddress", undefined, { shouldValidate: false });
        setValue("shippingAddressId", result.savedAddressId, {
          shouldValidate: true
        });

        // Trigger cart update for shipping options refresh
        const selectedAddress = addresses.find(
          (a) => a.id === result.savedAddressId
        );
        if (selectedAddress && onAddressChange) {
          onAddressChange(selectedAddress);
        }
      } else if (result.inlineAddress) {
        // One-time address - store inline data. The schema reports a missing
        // shipping address on `shippingAddressId` (validations.ts), so that
        // is the path to re-validate: validating `shippingAddress` left a
        // stale "Please select or enter a shipping address" under the select
        // after a valid one-time address was entered (KAN-230).
        setValue("shippingAddressId", "", { shouldValidate: false });
        setValue("shippingAddress", result.inlineAddress, {
          shouldValidate: false
        });
        void trigger("shippingAddressId");

        // Update phone if provided
        if (result.inlineAddress.phone) {
          setValue("phone", result.inlineAddress.phone, {
            shouldValidate: false
          });
        }

        // Trigger cart update for shipping options refresh
        if (onAddressChange) {
          onAddressChange(result.inlineAddress);
        }
      }
    },
    [addresses, setValue, trigger, onAddressChange]
  );

  return (
    <div className="space-y-6">
      {/* Contact Information */}
      <div>
        {/* <h3 className="mb-3 text-sm font-medium text-gray-900">
          Contact Information
        </h3> */}
        <ContactInformation
          form={form}
          disabled={disabled}
          isLoading={isLoading}
        />
      </div>

      {/* Shipping Address Selection */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-gray-900">
          Shipping address <span className="text-red-500">*</span>
        </h3>
        <AddressDropdown
          type="shipping"
          addresses={addresses}
          selectedId={shippingAddressId || null}
          customAddress={shippingAddress}
          onSelect={handleSelectAddress}
          onSelectCustom={handleSelectCustom}
          onAddNew={handleAddNewAddress}
          onEdit={handleEditAddress}
          disabled={disabled}
          isLoading={isLoading}
          error={errors.shippingAddressId?.message}
          placeholder="Select a shipping address"
        />
      </div>

      {/* Add Address Dialog */}
      <CheckoutAddressDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        type="shipping"
        isFirstAddress={addresses.length === 0}
        onSuccess={handleAddressSuccess}
      />

      {/* Edit Address Dialog */}
      <AddressDialog
        open={!!editingAddress}
        onOpenChange={handleEditDialogClose}
        address={editingAddress}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
});
